// hooks/useSearch.js
import { useState, useRef, useEffect, useCallback } from "react";

// Get API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
// Placeholder image for fallbacks
const PLACEHOLDER_IMAGE = process.env.REACT_APP_PLACEHOLDER_IMAGE || 'https://via.placeholder.com/50';

/**
 * Custom hook for handling search functionality across the application
 * @param {string} initialQuery - Initial search query
 * @param {Object} options - Optional configuration
 * @param {boolean} options.searchManga - Whether to search manga (default: true)
 * @param {number} options.debounceTime - Debounce delay in milliseconds (default: 300)
 * @param {number} options.resultLimit - Maximum number of results per category (default: 5)
 * @returns {Object} Search state and handlers
 */
export const useSearch = (initialQuery = '', options = {}) => {
  // Default options
  const {
    searchManga = true,
    debounceTime = 300,
    resultLimit = 5
  } = options;
  
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [results, setResults] = useState({
    anime: [],
    manga: [],
    characters: []
  });
  const [isSearching, setIsSearching] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeoutRef = useRef(null);
  const currentQueryRef = useRef("");

  // Helper for conditional logging
  const logDebug = (message, ...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[useSearch] ${message}`, ...args);
    }
  };
  
  const logError = (message, ...args) => {
    console.error(`[useSearch] ${message}`, ...args);
    setError(message);
  };

  // Clean up any timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Clear search results and state
  const clearResults = useCallback(() => {
    setResults({ anime: [], manga: [], characters: [] });
    setHasTyped(false);
    setError(null);
    currentQueryRef.current = "";
  }, []);

  // Fetch search results from the API
  const fetchSearchResults = useCallback(async (query) => {
    // Store the current query being processed
    currentQueryRef.current = query;
    
    if (!query.trim()) {
      clearResults();
      return;
    }

    setHasTyped(true);
    setIsSearching(true);
    setError(null);
    
    try {
      const localQueryCopy = query; // Create a local copy for comparison later
      
      // Create array of promises for API calls
      const promises = [
        fetch(`${API_BASE_URL}/api/anime/search?query=${encodeURIComponent(query)}`),
        searchManga ? fetch(`${API_BASE_URL}/api/manga/search?query=${encodeURIComponent(query)}`) : Promise.resolve(null),
        fetch(`${API_BASE_URL}/api/characters/search?query=${encodeURIComponent(query)}`)
      ];
      
      // Wait for all requests to complete
      const [animeRes, mangaRes, charactersRes] = await Promise.all(promises);

      // Check if the query is still relevant after all calls complete
      if (localQueryCopy !== currentQueryRef.current) {
        logDebug(`Search results for "${localQueryCopy}" ignored because newer query exists`);
        return;
      }

      // Process anime results
      let animeData = [];
      if (animeRes && animeRes.ok) {
        try {
          animeData = await animeRes.json();
          logDebug(`Found ${animeData.length} anime results for "${query}"`);
        } catch (error) {
          logError("Error parsing anime search results:", error);
        }
      } else if (animeRes) {
        logError(`Anime search failed with status: ${animeRes.status}`);
      }
      
      // Check again if query is still relevant
      if (localQueryCopy !== currentQueryRef.current) return;
      
      // Process manga results
      let mangaData = [];
      if (searchManga && mangaRes && mangaRes.ok) {
        try {
          mangaData = await mangaRes.json();
          logDebug(`Found ${mangaData.length} manga results for "${query}"`);
        } catch (error) {
          logError("Error parsing manga search results:", error);
        }
      } else if (searchManga && mangaRes) {
        logError(`Manga search failed with status: ${mangaRes.status}`);
      }
      
      // Check again if query is still relevant
      if (localQueryCopy !== currentQueryRef.current) return;
      
      // Process character results
      let charactersData = [];
      if (charactersRes && charactersRes.ok) {
        try {
          charactersData = await charactersRes.json();
          logDebug(`Found ${charactersData.length} character results for "${query}"`);
        } catch (error) {
          logError("Error parsing character search results:", error);
        }
      } else if (charactersRes) {
        logError(`Character search failed with status: ${charactersRes.status}`);
      }
      
      // Final check before updating state
      if (localQueryCopy !== currentQueryRef.current) return;

      // Update the results state
      setResults({
        anime: animeData.slice(0, resultLimit),
        manga: searchManga ? mangaData.slice(0, resultLimit) : [],
        characters: charactersData.slice(0, resultLimit)
      });
      
      logDebug(`Set search results for "${query}"`);
    } catch (error) {
      logError("Error fetching search results:", error);
    } finally {
      // Only clear loading state if this is still the current query
      if (query === currentQueryRef.current) {
        setIsSearching(false);
      }
    }
  }, [clearResults, searchManga, resultLimit]);

  // Debounced search function with robust race condition handling
  const debouncedSearch = useCallback((query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      fetchSearchResults(query);
    }, debounceTime);
  }, [fetchSearchResults, debounceTime]);

  // Handle search query change with proper validation
  const handleQueryChange = useCallback((value) => {
    setSearchQuery(value);
    
    if (!value || !value.trim()) {
      clearResults();
      return;
    }
    
    // Update the current query ref immediately to track newest query
    currentQueryRef.current = value;
    debouncedSearch(value);
  }, [clearResults, debouncedSearch]);

  // Check if we have any search results
  const hasResults = results.anime.length > 0 || 
                    results.manga.length > 0 || 
                    results.characters.length > 0;

  // Only show results if the user has typed something AND there are results
  const showResults = hasTyped && hasResults;

  // Handle image loading errors
  const handleImageError = useCallback((e) => {
    e.target.src = PLACEHOLDER_IMAGE;
  }, []);

  return {
    // State
    query: searchQuery,
    results,
    isSearching,
    hasTyped,
    hasResults,
    showResults,
    error,
    currentQuery: currentQueryRef.current,
    
    // Actions
    setQuery: handleQueryChange,
    clearResults,
    handleImageError,
    
    // Constants
    placeholderImage: PLACEHOLDER_IMAGE
  };
};

export default useSearch;